import { EventEmitter } from '../utils/EventEmitter';
import { POICategory } from '../types';
import { CATEGORY_DEFINITIONS, generateColorFromString } from '../constants/Categories';

export interface POILayerState {
  id: string;
  name: string;
  category: POICategory;
  subcategory: string;
  visible: boolean;
  count: number;
  icon: string;
  color: string;
}

export interface POILayerGroup {
  id: string;
  name: string;
  category: POICategory;
  visible: boolean;
  icon: string;
  color: string;
  subcategories: POILayerState[];
  totalCount: number;
}

export interface POILayerEvents {
  'layer-visibility-changed': { layerId: string; visible: boolean; affectedPOIs: string[] };
  'group-visibility-changed': { groupId: string; visible: boolean; affectedLayers: string[] };
  'all-layers-visibility-changed': { visible: boolean };
  'layer-state-updated': { layers: POILayerGroup[] };
}

// Category definitions and shared helpers now live in src/constants/Categories.ts (DUP-12).
// Icons/colors here always matched the other copies; descriptions were abbreviated but never read.

// Get icon based on category name keywords
// NOTE (DUP-12): intentionally NOT the shared Categories.getIconForCategory — this copy
// historically lacked the 'display', 'intercom' and 'table' keywords, and switching to the
// shared version would change fallback icons for unknown categories in layer groups and
// panorama markers. Kept local to preserve behavior exactly.
const getIconForCategory = (categoryName: string): string => {
  const lower = (categoryName || '').toLowerCase();
  if (lower.includes('audio') || lower.includes('visual') || lower.includes('tv')) return '📺';
  if (lower.includes('communication') || lower.includes('phone')) return '📡';
  if (lower.includes('data') || lower.includes('network')) return '🖥️';
  if (lower.includes('electric') || lower.includes('power')) return '⚡';
  if (lower.includes('light') || lower.includes('lamp')) return '💡';
  if (lower.includes('fire') || lower.includes('alarm') || lower.includes('smoke')) return '🚨';
  if (lower.includes('security') || lower.includes('camera')) return '🔐';
  if (lower.includes('plumb') || lower.includes('pipe') || lower.includes('water')) return '🚰';
  if (lower.includes('hvac') || lower.includes('duct') || lower.includes('air')) return '🌡️';
  if (lower.includes('mechanical') || lower.includes('equipment')) return '⚙️';
  if (lower.includes('furniture') || lower.includes('chair')) return '🪑';
  if (lower.includes('door')) return '🚪';
  if (lower.includes('window')) return '🪟';
  if (lower.includes('wall') || lower.includes('structure')) return '🏗️';
  return '📦';
};

// Get complete category info - matches SystemsPage exactly
export const getCategoryInfo = (categoryName: string): { icon: string; color: string } => {
  if (CATEGORY_DEFINITIONS[categoryName]) {
    return { icon: CATEGORY_DEFINITIONS[categoryName].icon, color: CATEGORY_DEFINITIONS[categoryName].color };
  }
  return {
    icon: getIconForCategory(categoryName),
    color: generateColorFromString(categoryName)
  };
};

/**
 * Centralized POI Layer Management Service
 * Manages POI layer visibility and state across all viewers (3D, 2D, Panorama)
 * Now uses actual IFC category names to match Systems page hierarchy exactly
 */
export class POILayerManager extends EventEmitter<POILayerEvents> {
  private static instance: POILayerManager;
  private layerGroups: Map<string, POILayerGroup> = new Map();
  private poiToLayer: Map<string, string> = new Map(); // Map POI IDs to layer IDs
  private allLayersVisible: boolean = true;
  private layerPreferences: Map<string, boolean> = new Map();

  private constructor() {
    super();
    this.loadLayerPreferences();
  }

  public static getInstance(): POILayerManager {
    if (!POILayerManager.instance) {
      POILayerManager.instance = new POILayerManager();
    }
    return POILayerManager.instance;
  }

  /**
   * Update POI data and organize into layers
   * Uses Systems page hierarchy: user-assigned system_category takes priority over IFC categories
   */
  public updatePOIData(pois: Array<{
    id: string;
    title: string;
    poi_group_id: string;
    poi_group_name: string;
    poi_type_id: string;
    poi_type_name: string;
    poi_type_icon: string;
    ifc_category: string;
    // User-assigned system hierarchy from "Set Systems" feature
    system_category?: string;
    system_subcategory?: string;
    system_name?: string;
    visibility_check: boolean;
  }>): void {
    // Clear existing layer groups - we rebuild dynamically from actual categories
    this.layerGroups.clear();
    this.poiToLayer.clear();

    // Group POIs by category using Systems page hierarchy
    // Priority: user-assigned system_category > IFC category (exactly like Systems page)
    const categoryMap = new Map<string, {
      pois: string[];
      categoryName: string;
    }>();

    pois.forEach(poi => {
      // Use Systems page hierarchy: systemCategory > IFC category
      const categoryName = poi.system_category || poi.ifc_category || poi.poi_type_name || 'Other';
      const categoryId = categoryName.toLowerCase().replace(/\s+/g, '-');

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          pois: [],
          categoryName: categoryName
        });
      }

      categoryMap.get(categoryId)!.pois.push(poi.id);
      this.poiToLayer.set(poi.id, categoryId);
    });

    // Create layer groups from actual categories (each IFC category = one group)
    categoryMap.forEach((data, categoryId) => {
      const categoryInfo = getCategoryInfo(data.categoryName);

      const layerGroup: POILayerGroup = {
        id: categoryId,
        name: data.categoryName,
        category: POICategory.UTILITY, // Generic - actual category is in the name
        visible: this.layerPreferences.get(categoryId) ?? true,
        icon: categoryInfo.icon,
        color: categoryInfo.color,
        subcategories: [], // No subcategories - flat structure like Systems page
        totalCount: data.pois.length
      };

      this.layerGroups.set(categoryId, layerGroup);
    });

    // Sort groups by count (largest first)
    const sortedGroups = Array.from(this.layerGroups.values())
      .sort((a, b) => b.totalCount - a.totalCount);

    this.layerGroups.clear();
    sortedGroups.forEach(group => {
      this.layerGroups.set(group.id, group);
    });

    this.emit('layer-state-updated', { layers: Array.from(this.layerGroups.values()) });
  }

  /**
   * Toggle visibility of entire layer group (same as toggleLayerVisibility in flat structure)
   */
  public toggleGroupVisibility(groupId: string): void {
    this.toggleLayerVisibility(groupId);
  }

  /**
   * Toggle visibility of a specific layer (category in flat structure)
   */
  public toggleLayerVisibility(layerId: string): void {
    const group = this.layerGroups.get(layerId);
    if (!group) return;

    group.visible = !group.visible;

    // Collect affected POI IDs
    const affectedPOIs: string[] = [];
    this.poiToLayer.forEach((poiLayerId, poiId) => {
      if (poiLayerId === layerId) {
        affectedPOIs.push(poiId);
      }
    });

    this.layerPreferences.set(layerId, group.visible);
    this.saveLayerPreferences();

    this.emit('layer-visibility-changed', { layerId, visible: group.visible && this.allLayersVisible, affectedPOIs });
    this.emit('layer-state-updated', { layers: Array.from(this.layerGroups.values()) });
  }

  /**
   * Get visibility state of a specific layer
   */
  public getLayerVisibility(layerId: string): boolean {
    const group = this.layerGroups.get(layerId);
    if (group) {
      return group.visible && this.allLayersVisible;
    }
    return false;
  }

  /**
   * Get visibility state of a specific POI
   */
  public getPOIVisibility(poiId: string): boolean {
    const layerId = this.poiToLayer.get(poiId);
    return layerId ? this.getLayerVisibility(layerId) : false;
  }

  /**
   * Toggle visibility of all POI layers
   */
  public toggleAllLayersVisibility(): void {
    this.allLayersVisible = !this.allLayersVisible;
    this.emit('all-layers-visibility-changed', { visible: this.allLayersVisible });
  }

  /**
   * Get all layer groups with current state
   */
  public getLayerGroups(): POILayerGroup[] {
    return Array.from(this.layerGroups.values());
  }

  /**
   * Get the current all-layers visibility state
   */
  public getAllLayersVisible(): boolean {
    return this.allLayersVisible;
  }

  /**
   * Get POI IDs that should be visible based on current layer settings
   */
  public getVisiblePOIs(): string[] {
    if (!this.allLayersVisible) return [];

    const visiblePOIs: string[] = [];
    this.poiToLayer.forEach((layerId, poiId) => {
      if (this.getLayerVisibility(layerId)) {
        visiblePOIs.push(poiId);
      }
    });
    return visiblePOIs;
  }

  /**
   * Load layer preferences from localStorage
   */
  private loadLayerPreferences(): void {
    try {
      const saved = localStorage.getItem('mabat3d_poi_layer_preferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        Object.entries(preferences).forEach(([key, value]) => {
          this.layerPreferences.set(key, value as boolean);
        });
      }
    } catch (error) {
      console.warn('Failed to load POI layer preferences:', error);
    }
  }

  /**
   * Save layer preferences to localStorage
   */
  private saveLayerPreferences(): void {
    try {
      const preferences: Record<string, boolean> = {};
      this.layerPreferences.forEach((value, key) => {
        preferences[key] = value;
      });
      localStorage.setItem('mabat3d_poi_layer_preferences', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save POI layer preferences:', error);
    }
  }

  /**
   * Get all categories flattened (now returns groups directly since we use actual IFC categories)
   */
  public getFlattenedPOITypes(): Array<POILayerState & { groupName: string; groupId: string; groupIcon: string; groupColor: string }> {
    // With the new flat structure, each group IS a category (no subcategories)
    // Return groups as flattened types for compatibility with UI components
    const flattenedTypes: Array<POILayerState & { groupName: string; groupId: string; groupIcon: string; groupColor: string }> = [];

    this.layerGroups.forEach(group => {
      flattenedTypes.push({
        id: group.id,
        name: group.name,
        category: group.category,
        subcategory: group.name, // Same as name in flat structure
        visible: group.visible,
        count: group.totalCount,
        icon: group.icon,
        color: group.color,
        // Group info (same as the type itself in flat structure)
        groupName: group.name,
        groupId: group.id,
        groupIcon: group.icon,
        groupColor: group.color
      });
    });

    // Sort by count (largest first) - already sorted in updatePOIData
    return flattenedTypes;
  }

  /**
   * Get POIs for a specific subcategory/type
   */
  public getPOIsForSubcategory(subcategoryId: string): string[] {
    const poiIds: string[] = [];
    this.poiToLayer.forEach((layerId, poiId) => {
      if (layerId === subcategoryId) {
        poiIds.push(poiId);
      }
    });
    return poiIds;
  }

  /**
   * Get grouped structure for category headers
   */
  public getCategoryHeaders(): Array<{ id: string; name: string; icon: string; color: string; totalCount: number; visible: boolean }> {
    return Array.from(this.layerGroups.values()).map(group => ({
      id: group.id,
      name: group.name,
      icon: group.icon,
      color: group.color,
      totalCount: group.totalCount,
      visible: group.visible
    }));
  }

  /**
   * Reset all layer preferences to defaults (all visible)
   */
  public resetToDefaults(): void {
    this.layerPreferences.clear();
    localStorage.removeItem('mabat3d_poi_layer_preferences');
    // Set all current groups to visible
    this.layerGroups.forEach(group => {
      group.visible = true;
    });
    this.allLayersVisible = true;
    this.emit('layer-state-updated', { layers: Array.from(this.layerGroups.values()) });
  }
}

// Export singleton instance
export const poiLayerManager = POILayerManager.getInstance();